import { useEffect, useRef, useState } from 'react'
import { audioTimeOfEvent, setPlaybackSession, unlockAudio } from '../../platform/audio'
import { ClickQueue, scheduleClick } from '../../transport/metronome'
import { measureLatency, type LatencyMeasure } from '../../transport/tap'
import common from '../common.module.css'
import { updateDeviceSettings, useDeviceSettings } from '../device'
import styles from '../screens/Settings.module.css'
import { t } from '../strings'

/** 75 BPM: un clic cada 0,8 s, así un retraso de hasta 400 ms no se confunde de clic. */
const PERIOD = 0.8
const CLICKS = 16
const MIN_MS = -100
const MAX_MS = 1000

interface Session {
  ctx: AudioContext
  clicks: number[]
  taps: number[]
  queue: ClickQueue
}

/**
 * Calibración de la latencia (§5.2): suenan unos clics y tocas a la vez que los oyes. La mediana
 * del retraso entre el clic programado y tu toque es lo que tarda el sonido en llegarte (más tu
 * reacción). La guía la descuenta al resaltar y al grabar cambios. Vale solo para este dispositivo.
 */
export function Calibration() {
  const { latencyMs, calibratedAt } = useDeviceSettings()
  const c = t.calibration
  const session = useRef<Session | null>(null)
  const [phase, setPhase] = useState<'idle' | 'listening' | 'done'>('idle')
  const [taps, setTaps] = useState(0)
  const [result, setResult] = useState<LatencyMeasure | null>(null)

  const start = async () => {
    // Lo que exige un toque, antes de esperar a nada (iOS).
    const unlocking = unlockAudio()
    void setPlaybackSession(true)
    const ctx = await unlocking
    const first = ctx.currentTime + 0.6
    const queue = new ClickQueue()
    const clicks = Array.from({ length: CLICKS }, (_, index) => first + index * PERIOD)
    clicks.forEach((time, index) =>
      queue.add(scheduleClick(ctx, ctx.destination, time, { accent: index % 4 === 0 })),
    )
    session.current = { ctx, clicks, taps: [], queue }
    setTaps(0)
    setResult(null)
    setPhase('listening')
  }

  // Se acaba solo poco después del último clic.
  useEffect(() => {
    if (phase !== 'listening') return
    const timer = window.setTimeout(
      () => {
        const current = session.current
        if (!current) return
        setResult(measureLatency(current.clicks, current.taps, PERIOD))
        setPhase('done')
        void setPlaybackSession(false)
      },
      (0.6 + CLICKS * PERIOD + 0.5) * 1000,
    )
    return () => window.clearTimeout(timer)
  }, [phase])

  const tap = (timeStamp: number) => {
    const current = session.current
    if (!current || phase !== 'listening') return
    current.taps.push(audioTimeOfEvent(current.ctx, timeStamp))
    setTaps(current.taps.length)
  }

  useEffect(() => {
    if (phase !== 'listening') return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== ' ' || event.repeat) return
      event.preventDefault()
      const current = session.current
      if (!current) return
      current.taps.push(audioTimeOfEvent(current.ctx, event.timeStamp))
      setTaps(current.taps.length)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase])

  const cancel = () => {
    session.current?.queue.cancelPending(session.current.ctx.currentTime)
    session.current = null
    setPhase('idle')
    void setPlaybackSession(false)
  }

  const save = (ms: number) => {
    updateDeviceSettings({
      latencyMs: Math.min(MAX_MS, Math.max(MIN_MS, Math.round(ms))),
      calibratedAt: new Date().toISOString(),
    })
    setPhase('idle')
  }

  return (
    <section className={common.card}>
      <h2>{c.title}</h2>
      <p className={common.muted}>{c.text}</p>
      <p>{latencyMs !== undefined ? c.current(latencyMs) : c.automatic}</p>
      {phase === 'listening' ? (
        <button
          className={styles.calibrationPad}
          onPointerDown={(event) => {
            event.preventDefault()
            tap(event.timeStamp)
          }}
        >
          {c.tapHere}
          <span className={common.small}>{c.taps(taps)}</span>
        </button>
      ) : (
        <div className={common.row}>
          <button className={common.toggleOn} onClick={() => void start()}>
            {c.start}
          </button>
          {latencyMs !== undefined && (
            <>
              <span className={styles.label}>{c.adjust}</span>
              <button onClick={() => save(latencyMs - 10)}>−10 ms</button>
              <button onClick={() => save(latencyMs + 10)}>+10 ms</button>
              <button onClick={() => updateDeviceSettings({})}>{c.reset}</button>
            </>
          )}
        </div>
      )}
      {phase === 'listening' && (
        <div className={common.row} style={{ marginTop: 8 }}>
          <button onClick={cancel}>{c.cancel}</button>
        </div>
      )}
      {phase === 'done' && result && (
        <div style={{ marginTop: 8 }}>
          {result.offset !== null ? (
            <div className={common.row}>
              <span>
                {c.result(Math.round(result.offset * 1000), Math.round(result.spread * 1000))}
              </span>
              <button className={common.toggleOn} onClick={() => save(result.offset! * 1000)}>
                {c.save}
              </button>
              <button onClick={() => setPhase('idle')}>{c.cancel}</button>
            </div>
          ) : (
            <p className={common.noticeWarn}>{c.unreliable}</p>
          )}
        </div>
      )}
      {calibratedAt && latencyMs !== undefined && (
        <p className={`${common.muted} ${common.small}`}>
          {new Date(calibratedAt).toLocaleString('es-ES')}
        </p>
      )}
    </section>
  )
}
